import { StrictLocalizeFunction } from "@faire/web--source/common/localization";
import {
  TwoLetterStateCode,
  TwoLetterStateCodesThatRequireResellerID,
} from "@faire/web--source/common/types/StateCodes";

export interface IIdOption {
  label: string;
  placeholder: string;
  id: "ein" | "drivers_license_number" | "ssn" | "certificate_number";
}

export interface IFormData {
  id: number;
  expirationDateRequired: boolean;
  hasTerms: boolean;
  taxAccountIdNumber: boolean;
  idOptions: IIdOption[];
}

export const STATES_WITHOUT_SALES_TAX: readonly TwoLetterStateCode[] = [
  "AK",
  "DE",
  "MT",
  "NH",
  "OR",
] as const;

type TwoLetterStateCodesThatRequireResellerCertificate = Exclude<
  TwoLetterStateCode,
  (typeof STATES_WITHOUT_SALES_TAX)[number]
>;

const GROUP_1: TwoLetterStateCode[] = [
  "CO",
  "CT",
  "HI",
  "IN",
  "KS",
  "KY",
  "MA",
  "NE",
  "NJ",
  "NM",
  "NV",
  "RI",
  "SC",
  "VA",
];
const GROUP_2A: TwoLetterStateCode[] = [
  "AL",
  "AR",
  "CA",
  "MS",
  "NC",
  "WI",
  "WV",
];
const GROUP_2B: TwoLetterStateCode[] = ["AZ", "FL", "IL", "ME", "MO", "OK"];
const GROUP_3A: TwoLetterStateCode[] = [
  "GA",
  "IA",
  "MN",
  "ND",
  "OH",
  "SD",
  "TN",
  "UT",
  "VT",
];
const GROUP_3B: TwoLetterStateCode[] = ["MI", "WA", "LA"];
const GROUP_4: TwoLetterStateCode[] = ["ID", "MD", "PA", "TX", "NY"];
const GROUP_5: TwoLetterStateCode[] = ["WY"];
const GROUP_6: TwoLetterStateCode[] = ["DC"];

const STATE_TO_PLACEHOLDER: {
  [key in TwoLetterStateCodesThatRequireResellerID]: string;
} = {
  AL: "SLS-A123412341",
  AZ: "12341234",
  AR: "12341234-SLS",
  CA: "123-123412",
  CO: "12341234",
  CT: "1234123-123",
  DC: "351234123412",
  FL: "12-1234123412-1",
  GA: "123-123412",
  HI: "GE-123-123-1234-12",
  ID: "001234123",
  IL: "1234-1234",
  IN: "0 123412341",
  IA: "1-12-123412",
  KS: "004-123412341F-12",
  KY: "12341",
  LA: "1234123412",
  ME: "1234123",
  MD: "12341234",
  MA: "SLS-12341234-123",
  MI: "12-1234123",
  MN: "1234123",
  MS: "123-12341-1",
  MO: "12341234",
  NE: "12341234",
  NV: "1234123412",
  NJ: "123-123-123",
  NM: "12-123412-12-1",
  NY: "12-3456789 or TF-1234567",
  NC: "123412341",
  ND: "123412-00",
  OH: "01-123412",
  OK: "STS-12341234-12",
  PA: "12341234",
  RI: "123412341",
  SC: "123-12341-1",
  SD: "1234-1234-ST",
  TN: "123412341",
  TX: "12341234123",
  UT: "12341234-123-ABC",
  VT: "SUT-12341234",
  VA: "12-123412341F-123",
  WA: "A12341234",
  WV: "1234-1234",
  WI: "456-1234123412-12",
  WY: "12341234",
};

const FEIN_PLACEHOLDER = "12-3412341";
const SSN_PLACEHOLDER = "123-41-1234";
const DRIVER_LICENSE_PLACEHOLDER = "123412-341";

const STATE_TO_SPECIFIED_DOCUMENT_NAME: {
  [key in TwoLetterStateCodesThatRequireResellerCertificate]: string;
} = {
  AL: "Sales tax license",
  AZ: "Transaction privilege tax license",
  AR: "Standard permit",
  CA: "Seller’s permit",
  CO: "Sales tax license",
  CT: "Sales and use tax permit",
  DC: "Certificate of registration for sales and use tax",
  FL: "Florida annual resale certificate for sales tax",
  GA: "Sales and use tax certificate of registration",
  HI: "General excise tax license",
  ID: "Seller’s permit",
  IL: "Illinois business authorization/certificate of registration",
  IN: "Registered retail merchant certificate",
  IA: "Retail sales tax permit",
  KS: "Retailers' sales tax registration",
  KY: "Sales and use tax permit",
  LA: "Louisiana resale certificate",
  ME: "Resale certificate",
  MD: "Sales and use tax license",
  MA: "Sales and use tax registration certificate",
  MI: "Sales tax license",
  MN: "Tax id number registration",
  MS: "Retail sales tax permit",
  MO: "Retail sales license",
  NE: "Sales tax permit",
  NV: "Sales tax permit",
  NJ: "Certificate of authority",
  NM: "Registration certificate",
  NY: "Certificate of authority",
  NC: "Certificate of registration for sales and use tax",
  ND: "Sales and use tax permit",
  OH: "Vendor’s license",
  OK: "Sales tax permit",
  PA: "Sales tax license",
  RI: "Retail sales permit",
  SC: "Retail license",
  SD: "Sales tax license",
  TN: "Certificate of registration: sales & use",
  TX: "Sales and use tax permit",
  UT: "Sales tax license",
  VT: "Sales and use tax license",
  VA: "Sales and use tax certificate",
  WA: "Reseller permit",
  WV: "Business registration certificate",
  WI: "Seller’s permit",
  WY: "Sales/use tax license",
};

const STATE_TO_SPECIFIED_ISSUING_BODY_NAME: {
  [key in TwoLetterStateCodesThatRequireResellerCertificate]: string;
} = {
  AL: "Alabama Department of Revenue",
  AZ: "Arizona Department of Revenue",
  AR: "Department of Finance and Administration",
  CA: "California Department of Tax and Fee Administration",
  CO: "Department of Revenue",
  CT: "Department of Revenue Services",
  DC: "Office of Tax and Revenue",
  FL: "Department of Revenue",
  GA: "Department of Revenue",
  HI: "Department of Taxation",
  ID: "Idaho State Tax Commission",
  IL: "Department of Revenue",
  IN: "Department of Revenue",
  IA: "Department of Revenue",
  KS: "Department of Revenue",
  KY: "Department of Revenue",
  LA: "Department of Revenue",
  ME: "Maine Revenue Services",
  MD: "Revenue Administration Division",
  MA: "Department of Revenue",
  MI: "Department of Treasury",
  MN: "Department of Revenue",
  MS: "Department of Revenue",
  MO: "Department of Revenue",
  NE: "Department of Revenue",
  NV: "Department of Taxation",
  NJ: "Division of Taxation",
  NM: "Taxation and Revenue Department",
  NY: "Department of Taxation and Finance",
  NC: "Department of Revenue",
  ND: "State of North Dakota",
  OH: "Department of Taxation",
  OK: "Tax Commission",
  PA: "Department of Revenue",
  RI: "Division of Taxation",
  SC: "Department of Revenue",
  SD: "Department of Revenue",
  TN: "Department of Revenue",
  TX: "Texas Comptroller",
  UT: "State Tax Commission",
  VT: "Department of Taxes",
  VA: "Department of Taxation",
  WA: "Department of Revenue",
  WV: "State Tax Department",
  WI: "Department of Revenue",
  WY: "Department of Revenue",
};

const EIN_ID_OPTION: IIdOption = {
  label: "FEIN",
  placeholder: FEIN_PLACEHOLDER,
  id: "ein",
};

const SSN_ID_OPTION: IIdOption = {
  label: "SSN",
  placeholder: SSN_PLACEHOLDER,
  id: "ssn",
};

const DRIVER_LICENSE_ID_OPTION: IIdOption = {
  label: "Driver’s license",
  placeholder: DRIVER_LICENSE_PLACEHOLDER,
  id: "drivers_license_number",
};

const resellerCertificateField = (
  state: string,
  strictLocalize: StrictLocalizeFunction
): IIdOption => ({
  label: getStateSpecificResellerIDName(state, strictLocalize),

  // @ts-expect-error FIXME(implicitAny): https://faire.link/no-implicit-any
  placeholder: STATE_TO_PLACEHOLDER[state],
  id: "certificate_number",
});

const flow3 = (
  state: string,
  strictLocalize: StrictLocalizeFunction
): IFormData => ({
  id: 3,
  expirationDateRequired: false,
  hasTerms: state === "NY",
  taxAccountIdNumber: false,
  idOptions: [resellerCertificateField(state, strictLocalize)],
});

const flow4 = (
  state: string,
  strictLocalize: StrictLocalizeFunction
): IFormData => ({
  id: 4,
  expirationDateRequired: true,
  hasTerms: false,
  taxAccountIdNumber: false,
  idOptions: [resellerCertificateField(state, strictLocalize)],
});

const flow5 = (
  state: string,
  strictLocalize: StrictLocalizeFunction
): IFormData => ({
  id: 5,
  expirationDateRequired: false,
  hasTerms: false,
  taxAccountIdNumber: false,
  idOptions: [
    resellerCertificateField(state, strictLocalize),
    EIN_ID_OPTION,
    DRIVER_LICENSE_ID_OPTION,
  ],
});

const flow7 = (): IFormData => ({
  id: 7,
  expirationDateRequired: false,
  hasTerms: false,
  taxAccountIdNumber: true,
  idOptions: [EIN_ID_OPTION, SSN_ID_OPTION],
});

export const getStateSpecificIssuingBodyName = (state: string) =>
  STATE_TO_SPECIFIED_ISSUING_BODY_NAME[
    state as TwoLetterStateCodesThatRequireResellerCertificate
  ];

export const getStateSpecificResellerCertificateName = (state: string) =>
  STATE_TO_SPECIFIED_DOCUMENT_NAME[
    state as TwoLetterStateCodesThatRequireResellerCertificate
  ];

export const getStateSpecificResellerIDName = (
  state: string,
  strictLocalize: StrictLocalizeFunction
) => {
  return strictLocalize(
    {
      defaultMessage: "{resellerCertificateFileName} ID",
      description: {
        text: "the ID number of reseller certificate document",
      },
    },
    {
      resellerCertificateFileName:
        getStateSpecificResellerCertificateName(state),
    }
  );
};

export const getResellerCertificateFormData = (
  state: string,
  strictLocalize: StrictLocalizeFunction
): IFormData | null => {
  if (
    (GROUP_1 as string[]).includes(state) ||
    (GROUP_2A as string[]).includes(state) ||
    (GROUP_3A as string[]).includes(state) ||
    (GROUP_4 as string[]).includes(state)
  ) {
    return flow3(state, strictLocalize);
  }

  if (
    (GROUP_2B as string[]).includes(state) ||
    (GROUP_3B as string[]).includes(state)
  ) {
    return flow4(state, strictLocalize);
  }

  if ((GROUP_5 as string[]).includes(state)) {
    return flow5(state, strictLocalize);
  }

  if ((GROUP_6 as string[]).includes(state)) {
    return flow7();
  }

  return null;
};
